import { useState, useRef, useEffect, FC, MutableRefObject, ReactElement } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { SxProps } from '@mui/material';
import { PopperPlacementType } from '@mui/base/Popper/Popper.types';

interface PopupProps {
    actionElement: (callback: () => void, anchorReference: MutableRefObject<any>, isOpen?: boolean) => ReactElement;
    contentElement: (callback: () => void, isOpen?: boolean) => ReactElement;
    onToggle?: () => void;
    popperStyles?: SxProps;
    placement?: PopperPlacementType;
    modifiers?: any;
    onClose?: () => void;
}

const Popup: FC<PopupProps> = ({ actionElement, contentElement, onToggle, popperStyles, placement, modifiers }) => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
        onToggle?.();
    };

    const handleClose = () => {
        if (open) {
            setOpen(false);
            onToggle?.();
        }
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    return (
        <div>
            {actionElement(handleToggle, anchorRef, open)}
            <Popper
                modifiers={modifiers}
                sx={{ ...{ width: 'max-content', zIndex: 4 }, ...popperStyles }}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                placement={placement || 'bottom-start'}
                transition
                disablePortal
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                {contentElement(handleClose, open)}
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </div>
    );
};

export default Popup;
